'use client';

import { useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { motion } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, Download, Copy } from 'lucide-react';
import { BACKDROP_BG_CLASS } from './backdrop';
import { Button } from './button';
import { ContextMenu, ContextMenuItem } from './context-menu';
import { useContextMenu } from './use-context-menu';
import { useEscapeDismiss } from './dismiss-stack';
import { useImageZoom } from './use-image-zoom';
import { useLightboxCarousel } from './use-lightbox-carousel';
import { useLightboxSwipeDismiss } from './use-lightbox-swipe-dismiss';
import { getYouTubeVideoId } from './youtube-embed';
import { useViewportSize } from '../lib/viewport-size/index';
import { toast } from '../lib/haptics/haptic-toast';
import { cn } from '../utils/cn';
// Deep-import the concrete module, not the `@naumu/shared` barrel - the barrel
// re-exports every schemas/*.ts and drags zod (~299 KiB) into any chunk that
// touches it, including the marketing shell's.
import type { MessageAttachmentDisplay } from '@naumu/shared/types/attachment';
import { isImageType } from '@naumu/shared/types/attachment';
import { attachmentDownloadUrl } from '../lib/attachment-url';
import { copyImageToClipboard, describeCopyImageError } from '../lib/copy-image-to-clipboard';
import { downloadFile } from '../lib/download-file';
import { AttachmentLightboxSlide } from './attachment-lightbox-slide';

/** Attachment with an optional visible caption (shown under the content). */
export type LightboxAttachment = MessageAttachmentDisplay & { caption?: string };

interface AttachmentLightboxProps {
	attachments: LightboxAttachment[];
	initialIndex: number;
	onClose: () => void;
}

export function AttachmentLightbox({ attachments, initialIndex, onClose }: AttachmentLightboxProps) {
	const { trackRef, currentIndex, scrollToIndex, onScroll } = useLightboxCarousel(
		attachments.length,
		initialIndex,
	);
	const current = attachments[currentIndex];
	const hasMultiple = attachments.length > 1;
	const currentYouTubeId = current ? getYouTubeVideoId(current.url) : null;
	// One shared zoom instance; its ref is bound to whichever slide is active.
	const zoom = useImageZoom(current?.url);
	const { isMobile } = useViewportSize();
	const contextMenu = useContextMenu();
	// Touch-only, and never while zoomed — a zoomed image owns vertical touch for panning.
	const swipe = useLightboxSwipeDismiss(isMobile && !zoom.isZoomed, onClose);

	const goNext = useCallback(() => scrollToIndex(currentIndex + 1), [scrollToIndex, currentIndex]);
	const goPrev = useCallback(() => scrollToIndex(currentIndex - 1), [scrollToIndex, currentIndex]);

	const handleDownload = useCallback(() => {
		if (!current) return;
		// Saving asks for the file, not the picture: `attachmentDownloadUrl` opts
		// a HEIC back out of the webp transcode this lightbox renders from.
		void downloadFile(attachmentDownloadUrl(current), current.fileName).catch(() =>
			toast.error(`Could not download ${current.fileName}`),
		);
	}, [current]);

	const handleCopyImage = useCallback(async () => {
		if (!current) return;
		try {
			await copyImageToClipboard(current.url);
			toast.success('Image copied');
		} catch (err) {
			toast.error(describeCopyImageError(err));
		}
	}, [current]);

	useEscapeDismiss(true, onClose);

	useEffect(() => {
		if (!hasMultiple) return;
		const handleKeyDown = (e: KeyboardEvent) => {
			// While focus is inside a VideoPlayer (`.mkv-root`), the player owns the
			// arrows — they seek and change volume there. Yield instead of swiping the
			// carousel out from under playback.
			const target = e.target;
			if (target instanceof Element && target.closest('.mkv-root')) return;
			// No wraparound with scroll-snap — a wrap jump would be jarring.
			if (e.key === 'ArrowRight') goNext();
			else if (e.key === 'ArrowLeft') goPrev();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [goNext, goPrev, hasMultiple]);

	if (!current) return null;

	const content = (
		<div
			ref={(el) => {
				zoom.surfaceRef.current = el;
				swipe.surfaceRef.current = el;
			}}
			className="fixed inset-0 z-[100]"
			onClick={(e) => {
				if (e.target === e.currentTarget) onClose();
			}}
		>
			{/* Backdrop is its own layer so the swipe-to-dismiss gesture can fade it
			    without fading the content it sits behind. */}
			<motion.div
				className={cn('pointer-events-none absolute inset-0', BACKDROP_BG_CLASS)}
				style={{ opacity: swipe.backdropOpacity }}
			/>

			{/* Horizontal scroll-snap carousel track. Covers the full surface so a
			    trackpad horizontal scroll anywhere over the lightbox moves it.
			    Locked to overflow-hidden while zoomed so image panning can't drift
			    it (programmatic scrollTo still works for the arrows/keys). */}
			<motion.div
				ref={trackRef}
				onScroll={onScroll}
				style={{ y: swipe.dragY, scale: swipe.contentScale }}
				className={cn(
					'absolute inset-0 flex snap-x snap-mandatory overscroll-x-contain scrollbar-hidden',
					zoom.isZoomed ? 'overflow-hidden' : 'overflow-x-auto',
				)}
			>
				{attachments.map((att, i) => (
					<AttachmentLightboxSlide
						key={att.id}
						attachment={att}
						zoom={zoom}
						isActive={i === currentIndex}
						isMounted={Math.abs(i - currentIndex) <= 1}
						isMobile={isMobile}
						onContextMenu={contextMenu.handleContextMenu}
						onBackdropClick={onClose}
					/>
				))}
			</motion.div>

			{/* Toolbar — fades out progressively as the dismiss gesture pulls the content away. */}
			<motion.div
				style={{ opacity: swipe.chromeOpacity }}
				className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between p-4"
			>
				<div className="text-sm text-neutral/70">
					{current.fileName}
					{hasMultiple && (
						<span className="ml-2 text-neutral/50">
							{currentIndex + 1} / {attachments.length}
						</span>
					)}
				</div>
				<div className="flex items-center gap-2">
					{!currentYouTubeId && (
						<Button
							variant="solid-muted"
							size="md"
							iconOnly
							onClick={handleDownload}
							aria-label="Download"
						>
							<Download />
						</Button>
					)}
					<Button
						variant="solid-muted"
						size="md"
						iconOnly
						onClick={onClose}
						aria-label="Close"
					>
						<X />
					</Button>
				</div>
			</motion.div>

			{/* Navigation arrows — no wraparound, so disable at the ends. */}
			{hasMultiple && (
				<motion.div
					style={{ opacity: swipe.chromeOpacity }}
					className="pointer-events-none absolute inset-0 z-10"
				>
					<Button
						variant="solid-muted"
						size="md"
						iconOnly
						onClick={goPrev}
						disabled={currentIndex === 0}
						className="pointer-events-auto absolute left-4 top-1/2 -translate-y-1/2"
						aria-label="Previous"
					>
						<ChevronLeft />
					</Button>
					<Button
						variant="solid-muted"
						size="md"
						iconOnly
						onClick={goNext}
						disabled={currentIndex === attachments.length - 1}
						className="pointer-events-auto absolute right-4 top-1/2 -translate-y-1/2"
						aria-label="Next"
					>
						<ChevronRight />
					</Button>
				</motion.div>
			)}

			{/* Right-click context menu (desktop) — Copy image / Download */}
			<ContextMenu
				open={contextMenu.open}
				position={contextMenu.position}
				onClose={contextMenu.close}
			>
				{isImageType(current.fileType) && (
					<ContextMenuItem
						onClick={() => {
							void handleCopyImage();
							contextMenu.close();
						}}
					>
						<Copy />
						Copy image
					</ContextMenuItem>
				)}
				<ContextMenuItem
					onClick={() => {
						handleDownload();
						contextMenu.close();
					}}
				>
					<Download />
					Download
				</ContextMenuItem>
			</ContextMenu>

			{/* Caption */}
			{current.caption && (
				<motion.div
					style={{ opacity: swipe.chromeOpacity }}
					className="pointer-events-none absolute bottom-0 left-0 right-0 z-10 p-4 text-center"
				>
					<span className="text-[13px] leading-[1.5] text-neutral/70">{current.caption}</span>
				</motion.div>
			)}
		</div>
	);

	return createPortal(content, document.body);
}
